import * as z from 'zod'
import { WriteTransaction } from 'replicache'
import {
  ListBlock,
  SpecificBlockData,
  blockTypesRecord,
  booleanBlockSchema,
  codeSnippetBlockSchema,
  colorBlockSchema,
  commentBlockSchema,
  componentBlockSchema,
  dateBlockSchema,
  documentBlockSchema,
  instanceBlockSchema,
  instanceTypeOptionsSchema,
  isComponentBlock,
  isDocumentBlock,
  isInstanceBlock,
  isListBlock,
  numberBlockSchema,
  referenceBlockSchema,
  richTextBlockSchema,
  selectBlockSchema,
  resolveInheritedHiddenState,
  textBlockSchema,
  normalizeStringPath,
  ogImageBlockSchema,
  variantSetBlockSchema,
  iconBlockSchema,
  eventBlockSchema,
  workflowBlockSchema,
  DEFAULT_LIST_VIEW,
  ListView,
  listViewLayoutsEnum,
  MediaBlock,
  mediaBlockSchema,
} from '@basehub/data/replicache/index'

import { getNewBlockDataByType } from '@basehub/data/replicache/helpers/get-new-block-data-by-type'
import { getIdempotentId } from '@basehub/utils/id/index'
import { createBlock, duplicateBlock, updateBlock } from './crud'
import { invariant } from '@epic-web/invariant'
import { reorderBlocks } from './reorder'
import { WithServerSecret } from '@basehub/data/replicache/tx/spec'
import { hasAccessOrThrow } from '@basehub/data/replicache/mutators/team-space/team/auth'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { doCopyOnWrite } from './copy-on-write'
import {
  jsonToPlainText,
  plainTextToYDocBase64State,
  richTextExtensionsForTransformations,
  stateToJson,
} from '@basehub/tiptap-api-utils'
import { BlockBatchMutationParams } from './batch'
import { resolveRichTextCustomChildBlocks } from './rich-text'
import { hashJson } from '@basehub/utils/hash'
import { updateListView } from './list'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { richTextOptions } from '@basehub/tiptap-api-utils/options'

const baseBlockSwapSchema = z.object({
  title: z.string().optional(),
  blockId: z.string(),
  path: z.string(),
  isoNow: z.string(),
  idSeed: z.string(),
  typeOptions: z.any(),
  hidden: z.boolean().optional(),
})

const swapToListBlockSchema = z.object({
  newBlockType: z.literal(blockTypesRecord.list),
  columnIds: z.array(z.string()),
  layout: listViewLayoutsEnum,
})

export const blockSwapSchema = z.discriminatedUnion('newBlockType', [
  swapToListBlockSchema,
  z.object({
    newBlockType: z.literal(blockTypesRecord.media),
    value: mediaBlockSchema.shape.value.optional(),
    preset: z.string().optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.boolean),
    value: booleanBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.codeSnippet),
    value: codeSnippetBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.comment),
    value: commentBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.date),
    value: dateBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.instance),
    value: instanceBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.number),
    value: numberBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.text),
    value: textBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.reference),
    value: referenceBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.select),
    value: selectBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.richText),
    value: richTextBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.component),
    value: componentBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.document),
    value: documentBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.color),
    value: colorBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.ogImage),
    value: ogImageBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.variantSet),
    value: variantSetBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.icon),
    value: iconBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.event),
    value: eventBlockSchema.shape.value.optional(),
  }),
  z.object({
    newBlockType: z.literal(blockTypesRecord.workflow),
    value: workflowBlockSchema.shape.value.optional(),
  }),
])
export type BlockSwap = z.infer<typeof blockSwapSchema>

const blockSwapParametersSchema = blockSwapSchema.and(baseBlockSwapSchema)
export type ChangeBlockTypeParams = z.infer<typeof blockSwapParametersSchema>

/* -------------------------------------------------------------------------------------------------
 * Change Block Type
 * -----------------------------------------------------------------------------------------------*/

/**
 * Creates a new block and deletes the old comment block.
 * Used when a comment block is converted to another type via the slash command.
 */
export async function changeBlockType(
  tx: WriteTransaction,
  { idSeed, ...blockSwap }: WithServerSecret<ChangeBlockTypeParams>
) {
  ensureBaseHubTransaction(tx)

  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: 'branch-author',
  })

  const { isoNow, newBlockType, typeOptions, title = '' } = blockSwap

  const { block, path, rootBlock, parentBlock } = await doCopyOnWrite(tx, {
    partialBlock: {
      id: blockSwap.blockId,
      // can't create a new block below an instance
      instanceChildTarget: null,
    },
    path: blockSwap.path,
    isoNow,
  })

  const blockId = block.id

  invariant(block, `Block with id ${blockId} not found`)
  invariant(
    parentBlock.type !== 'instance',
    'Parent block should not be an instance'
  )

  switch (block.type) {
    case 'document':
    case 'component':
    case 'list':
    case 'comment':
      break
    case 'rich-text':
      if (newBlockType !== 'text') {
        throw new MutatorUserError(
          `Can't change block type for ${block.type} to ${newBlockType}`
        )
      }
      break
    case 'text':
      if (newBlockType !== 'rich-text') {
        throw new MutatorUserError(
          `Can't change block type for ${block.type} to ${newBlockType}`
        )
      }
      break
    case 'instance':
      if (newBlockType !== 'list') {
        throw new MutatorUserError(
          `Can't change block type for ${block.type} to ${newBlockType}`
        )
      }
      break
    default:
      throw new MutatorUserError(`Can't change block type for ${block.type}`)
  }

  const undo: BlockBatchMutationParams['mutations'] = []

  // 1. create new block
  const newBlockId = (idSeed = getIdempotentId({ idSeed }))

  let blockSpecificStuff: null | SpecificBlockData = null

  switch (newBlockType) {
    case 'document':
    case 'component':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: blockSwap.value,
      })
      break
    case 'instance':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        typeOptions: instanceTypeOptionsSchema.parse(typeOptions),
      })
      break
    case 'list': {
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: {
          children: [],
          views: [
            {
              ...DEFAULT_LIST_VIEW,
              title: '',
              layout: blockSwap.layout,
              id: getIdempotentId({
                idSeed,
                splitPaths: { newPath: 'changeBlockType:new-view' },
              }),
              columnsMetadata: [
                {
                  blockId: '__title',
                  width: 200,
                  hidden: false,
                },
                ...blockSwap.columnIds.map((colId) => ({
                  blockId: colId,
                  width: 200,
                  hidden: false,
                })),
              ],
            },
          ],
        },
        /**
         * this type is wrong intentionally, as we don't yet have a component to target!
         * don't worry! the `createBlock` function will make sure to create the component for the list to target.
         * this should happen in this invocation, as it shouldn't be possible to create a block with typeOptions = null (cause zod).
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        typeOptions: {} as any,
      })
      break
    }
    case 'media': {
      const constraints: MediaBlock['constraints'] = {
        isRequired: false,
        allowedMimeTypes: null,
        maxSize: null,
      }
      if (blockSwap.preset) {
        switch (blockSwap.preset) {
          case 'all':
            constraints.allowedMimeTypes = {
              image: true,
              video: true,
              audio: true,
              file: true,
            }
            break
          case 'image':
            constraints.allowedMimeTypes = { image: true }
            break
          case 'video':
            constraints.allowedMimeTypes = { video: true }
            break
          case 'audio':
            constraints.allowedMimeTypes = { audio: true }
            break
          case 'file':
            constraints.allowedMimeTypes = { file: true }
            break
        }
      }
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        constraints,
        value: blockSwap.value,
      })
      break
    }
    case 'comment':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: blockSwap.value,
      })
      break
    case 'icon':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: blockSwap.value,
      })
      break
    case 'color':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: blockSwap.value,
      })
      break
    case 'og-image':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
      })
      break
    case 'text':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: blockSwap.value,
      })
      // is switching from rich-text to text
      if (block.type === 'rich-text') {
        blockSpecificStuff.value = jsonToPlainText(
          stateToJson(block.value.state) as Record<string, unknown>,
          richTextExtensionsForTransformations
        )

        if (block.variantOverrides) {
          blockSpecificStuff.variantOverrides = {}
          for (const [oKey, override] of Object.entries(
            block.variantOverrides
          )) {
            blockSpecificStuff.variantOverrides[oKey] = {
              value: jsonToPlainText(
                stateToJson(override.value.state) as Record<string, unknown>,
                richTextExtensionsForTransformations
              ),
            }
          }
        }
        blockSpecificStuff.constraints.isRequired = block.constraints.isRequired
        blockSpecificStuff.searchConfig = block.searchConfig
      }

      if (blockSpecificStuff.value === '') {
        blockSpecificStuff.value = null
      }

      break
    case 'date':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: blockSwap.value,
      })
      break
    case 'select':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: blockSwap.value,
      })
      break
    case 'code-snippet':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: blockSwap.value,
      })
      break
    case 'number':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: blockSwap.value,
      })
      break
    case 'reference':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: blockSwap.value,
      })
      break
    case 'rich-text':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
        value: blockSwap.value,
      })
      // is switching from text to rich-text
      if (block.type === 'text') {
        blockSpecificStuff.value.state = `!!RESET!!${
          (
            await plainTextToYDocBase64State(
              block.value ?? '',
              richTextExtensionsForTransformations
            )
          ).base64
        }`
        blockSpecificStuff.value.lastRevertedAt = isoNow

        if (block.variantOverrides) {
          blockSpecificStuff.variantOverrides = {}
          for (const [oKey, override] of Object.entries(
            block.variantOverrides
          )) {
            const newValue = {
              state: (
                await plainTextToYDocBase64State(
                  override.value ?? '',
                  richTextExtensionsForTransformations
                )
              ).base64,
            } satisfies (typeof blockSpecificStuff.variantOverrides)[string]['value']
            const { json } = await resolveRichTextCustomChildBlocks(tx, {
              state: newValue.state,
              children: [],
              constraints: blockSpecificStuff.constraints,
            })

            const hash = hashJson({ ...newValue, state: json })
            blockSpecificStuff.variantOverrides[oKey] = {
              hash,
              value: {
                state: `!!RESET!!${newValue.state}`,
              },
            }
          }
        }

        blockSpecificStuff.constraints = {
          isRequired: block.constraints.isRequired,
          forbiddenNodes: [...richTextOptions],
          allowedComponents: [],
          layout: 'single-line',
        }
        blockSpecificStuff.searchConfig = block.searchConfig
      }
      break
    case 'variant-set':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        value: blockSwap.value,
        title,
      })
      break
    case 'workflow':
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        value: blockSwap.value,
        title,
      })
      break
    default:
      blockSpecificStuff = getNewBlockDataByType({
        id: newBlockId,
        type: newBlockType,
        title,
      })
      break
  }

  const isChangingToList =
    newBlockType === 'list' && blockSwap.columnIds?.length

  let newBlock
  if (isChangingToList && blockSwap.newBlockType === 'list') {
    const firstColumn = await blockBaseOps.get(
      tx,
      blockSwap.columnIds?.[0] as string
    )
    const newListTargetBlockId =
      isChangingToList && firstColumn
        ? isComponentBlock(firstColumn)
          ? firstColumn.id
          : isInstanceBlock(firstColumn)
            ? firstColumn.typeOptions.targetBlockId
            : null
        : null

    const pathForCreation =
      firstColumn && isChangingToList
        ? path.split('/').slice(0, -1).join('/')
        : path

    const newBlockResult = await createBlock(tx, {
      data: {
        id: newBlockId,
        ...blockSpecificStuff,
        ...(firstColumn && isChangingToList
          ? { title: `Collection of ${firstColumn.title || 'untitled'}` }
          : {}),
        ...(newListTargetBlockId
          ? ({
              typeOptions: {
                targetBlockId: newListTargetBlockId,
              } satisfies ListBlock['typeOptions'],
            } as Record<string, unknown>)
          : {}),
      },
      idSeed: getIdempotentId({
        idSeed,
        splitPaths: { newPath: 'changeBlockType:new-block' },
      }),
      isoNow,
      path: pathForCreation,
      position: { type: 'after', targetId: blockId },
      parentInstanceChildTarget: parentBlock.instanceChildTarget,
    })

    newBlock = newBlockResult.newBlock
    undo.push(...newBlockResult.undo)
    invariant(isListBlock(newBlock), 'newBlock should be a list block')
    invariant(firstColumn, 'First column should exist')
    invariant(blockSwap.columnIds, 'Column ids should be defined')
    const columnBlocks = [firstColumn]

    if (tx.serverOnly) {
      const tm = await tx.getTreeManager(rootBlock.id)
      const parentNode = newBlockResult.newBlockRelationship?.parentId
        ? tm.getNode(newBlockResult.newBlockRelationship.parentId, {
            required: true,
          })
        : null
      tm.addNode(newBlock, {
        parentKey: parentNode?.key ?? '',
        order: newBlockResult.newBlockRelationship?.order,
      })
    }

    const listHasPreExistingTarget = !!newListTargetBlockId

    for (let index = 0; index < blockSwap.columnIds.length; index++) {
      const id = blockSwap.columnIds[index]

      invariant(id, 'Id should be defined')

      const block = index === 0 ? firstColumn : await blockBaseOps.get(tx, id)

      if (!block) continue
      const blockWithoutValue = { ...block, value: undefined }
      delete blockWithoutValue.value
      invariant(
        !isDocumentBlock(block) && !isListBlock(block),
        'This block type should not be changed'
      )

      // We've already added the first one
      if (index > 0) columnBlocks.push(block)

      const { undo: undoForThis } = await reorderBlocks(tx, {
        blockIds: [id],
        idSeed: (idSeed = getIdempotentId({ idSeed })),
        intendedIndex: 0,
        isoNow,
        targetBlockId:
          isComponentBlock(block) || isInstanceBlock(block)
            ? // we'll just reorder them below the list
              newBlock.id
            : // else below the template component
              newBlock.typeOptions.targetBlockId,
        rootId: rootBlock.id,
      })
      if (undoForThis) {
        undo.unshift(...undoForThis)
      }
    }

    const templateForList = await blockBaseOps.get(
      tx,
      newBlock.typeOptions.targetBlockId
    )

    invariant(
      templateForList && isComponentBlock(templateForList),
      'Template for list should be a component block'
    )

    // create the first row, only if firstColumn is not a component or instance and the operation is not a "create a list that targets X component."
    if (!listHasPreExistingTarget) {
      const dupResult = await duplicateBlock(tx, {
        duplicatedBlockId: newBlock.typeOptions.targetBlockId,
        newBlockId: (idSeed = getIdempotentId({ idSeed })),
        titleUpdateStrategy: 'untitled',
        duplicatedComponentStrategy: 'create-instance',
        isoNow,
        path: `${path}/${newBlock.id}`,
        idSeed: getIdempotentId({
          idSeed,
          splitPaths: { newPath: 'changeBlockType:change-block-row' },
        }),
      })
      if (dupResult?.undo) {
        undo.push(...dupResult.undo)
      }
    }
  } else {
    // we update the block type, but we don't delete the old block
    // so we can keep the id

    let componentForListBlockId: string | null = null
    let thumbnailForListBlockId: string | null = null

    if (blockSwap.newBlockType === 'list') {
      // create list's template
      componentForListBlockId = idSeed = getIdempotentId({ idSeed })
      if (blockSwap.layout === 'gallery') {
        thumbnailForListBlockId = idSeed = getIdempotentId({ idSeed })
      }
    }

    let hidden = !!blockSwap.hidden
    const result = await resolveInheritedHiddenState(
      { ...blockSpecificStuff, id: newBlockId },
      path,
      (id) => blockBaseOps.get(tx, id)
    )
    if (result.shouldInherit) {
      hidden = result.valueToInherit
    }

    const updateResult = await updateBlock(tx, {
      id: blockId,
      data: {
        ...blockSpecificStuff,
        ...(componentForListBlockId &&
          ({
            typeOptions: {
              targetBlockId: componentForListBlockId,
            } satisfies ListBlock['typeOptions'],
          } as Record<string, unknown>)),
        hidden,
        id: blockId,
      },
      strategy: 'merge',
      instanceChildTarget: null,
      path,
      isoNow,
      ignoreHiddenPropagationWarning: true,
      forceComponentInstancesSync: {
        idSeed: (idSeed = getIdempotentId({ idSeed })),
      },
    })
    newBlock = updateResult.updatedBlock
    undo.push(...updateResult.undo)

    if (componentForListBlockId) {
      // we need to create a component block to use in the list.
      const pathForComponentBlock = normalizeStringPath({
        path: updateResult.updatedPath,
        edgeId: newBlock.id,
        includeEdge: true,
        format: 'string',
      })
      const { newBlock: blockCreated } = await createBlock(tx, {
        data: {
          id: componentForListBlockId,
          title: '',
          type: 'component',
          hidden: true,
        },
        path: pathForComponentBlock,
        isoNow,
        position: { type: 'first' },
        idSeed: (idSeed = getIdempotentId({ idSeed })),
        parentInstanceChildTarget:
          updateResult.updatedBlock.instanceChildTarget,
      })
      // manual undo so we can skip checks such as "component can't be deleted as its the template of X list"
      undo.unshift({
        op: 'deleteBlock',
        args: {
          id: blockCreated.id,
          blocksAlsoBeingDeleted: [blockId],
          idSeed: (idSeed = getIdempotentId({ idSeed })),
          isoNow,
          path: pathForComponentBlock,
        },
      })
      invariant(
        blockCreated.type === 'component',
        `blockCreated ${blockCreated} should be of type "component". this is a bug in basehub`
      )

      if (thumbnailForListBlockId) {
        const pathForThumbnailBlock = normalizeStringPath({
          path: pathForComponentBlock,
          edgeId: blockCreated.id,
          includeEdge: true,
          format: 'string',
        })
        const { newBlock: thumbnailBlock } = await createBlock(tx, {
          data: {
            id: thumbnailForListBlockId,
            type: 'media',
          },
          path: pathForThumbnailBlock,
          isoNow,
          idSeed: (idSeed = getIdempotentId({ idSeed })),
          position: { type: 'last' },
          parentInstanceChildTarget: blockCreated.instanceChildTarget,
        })
        invariant(
          thumbnailBlock.type === 'media',
          `thumbnailBlock ${thumbnailBlock} should be of type "image". this is a bug in basehub`
        )

        if (
          !!thumbnailBlock &&
          newBlock.type === 'list' &&
          !!newBlock.value.views?.[0]
        ) {
          const { undo: undoListView } = await updateListView(tx, {
            blockId: newBlock.id,
            id: newBlock.value.views[0].id,
            data: {
              layout: 'gallery',
              thumbnailColumn: thumbnailBlock.id,
            } as Partial<ListView>,
            targetBlockId_withinTree: componentForListBlockId,
            idSeed: (idSeed = getIdempotentId({ idSeed })),
            path: updateResult.updatedPath,
            isoNow,
          })

          undo.push(...undoListView)
        }
      }
    }
  }

  return { newBlock, undo }
}
